// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { addToEndOfChain, ApiHandler } from "@insite/client-framework/HandlerCreator";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { chain as AddCartLinesChain } from "@insite/client-framework/Store/Data/Carts/Handlers/AddCartLines";
import { chain as AddLinesToCartChain } from "@insite/client-framework/Store/Pages/Cart/Handlers/AddLinesToCart";
import { chain as AddToCartChain } from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import { chain as AddWishListToCartChain } from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import { chain as ReorderOrderDetailsChain } from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/Reorder";
import { chain as ReorderOrderHistoryChain } from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import { chain as ReorderOrderStatusChain } from "@insite/client-framework/Store/Pages/OrderStatus/Handlers/Reorder";
import { chain as AddCartLineCollectionFromProductsChain } from "@insite/client-framework/Store/Pages/OrderUpload/Handlers/AddCartLineCollectionFromProducts";
import { chain as AddLinesToCartRequisitionsChain } from "@insite/client-framework/Store/Pages/Requisitions/Handlers/AddLinesToCart";
import { chain as PlaceOrderSavedOrderDetailsChain } from "@insite/client-framework/Store/Pages/SavedOrderDetails/Handlers/PlaceOrder";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { xGoogleAnalyticsState } from "@werner/App/GoogleAnalyticsConnector/Store/Reducers";
import { getDataLayer } from "@werner/App/GoogleAnalyticsConnector/Utilities/DataLayer";
import { generateMultipleCategories } from "@werner/App/GoogleAnalyticsConnector/Utilities/generateMultipleCategories";

type CartLineData = Pick<CartLineModel, "shortDescription" | "erpNumber" | "pricing" | "qtyOrdered" | "properties" | "brand">;

type HandlerType = ApiHandler<
    {
        cartLineCollection?: {
            cartLines?: CartLineModel[];
        };
        isQuickOrder?: boolean;
    },
    {
        cartLines?: CartLineModel[];
    } & CartLineData
>;

export const XngGaAddProductToCart: HandlerType = props => {
    const state = props.getState() as xGoogleAnalyticsState;
    const isQuickOrderFlyout = props.parameter.isQuickOrder;

    if (state.xGoogleAnalytics.addToCartEvent || (state.xGoogleAnalytics.quickOrderEvent && isQuickOrderFlyout)) {
        const dataLayer = getDataLayer();
        const session = getSession(state);
        const cart = getCartState(state, "current");

        if (!dataLayer) {
            return;
        }

        let cartLinesToAdd: CartLineData[] = [];
        if (props.apiResult?.cartLines || props.apiParameter?.cartLineCollection?.cartLines) {
            // XNG-Changes: this if condition is for Order History Reorder functionality
            if ((props.apiResult?.cartLines as any)?.cartLines) {
                cartLinesToAdd = (props.apiResult?.cartLines as any)?.cartLines || [];
            } else {
                cartLinesToAdd = props.apiResult?.cartLines || props.apiParameter.cartLineCollection?.cartLines || [];
            }
        } else {
            cartLinesToAdd = [
                {
                    shortDescription: props.apiResult.shortDescription || "",
                    erpNumber: props.apiResult.erpNumber || "",
                    pricing: props.apiResult.pricing || null,
                    qtyOrdered: props.apiResult.qtyOrdered || null,
                    properties: props.apiResult.properties,
                    brand: props.apiResult.brand,
                },
            ];
        }

        const ecommerceAnalyticsData = {
            ecommerce: {
                currency: state.context.session.currency?.currencyCode,
                value: cart.value?.orderSubTotal,
                affiliation: state.xGoogleAnalytics.affiliation,
                ...(props.apiResult?.pricing && { value: props.apiResult.pricing.extendedUnitNetPrice }),
                items: cartLinesToAdd.map((cartLine, idx) => {
                    const categoryFields = generateMultipleCategories(cartLine.properties?.["categories"]);
        
                    return {
                        item_id: cartLine.erpNumber,
                        item_name: cartLine.shortDescription,
                        affiliation: state.xGoogleAnalytics.affiliation,
                        discount: cartLine.pricing 
                            ? cartLine.pricing.unitNetPrice < cartLine.pricing.unitListPrice 
                                ? cartLine.pricing.unitListPrice - cartLine.pricing.unitNetPrice 
                                : 0 
                            : 0,
                        index: idx,
                        item_brand: state.xGoogleAnalytics.includeProductBrand
                            ? (cartLine as CartLineModel).brand?.name || ""
                            : undefined,
                        price: cartLine.pricing?.unitNetPrice,
                        quantity: cartLine.qtyOrdered,
                        item_list_id: state.xGoogleAnalytics.includeProductCategory
                            ? cartLine.properties?.["categoryId"] || ""
                            : undefined,
                        location_id: session.pickUpWarehouse?.id,
                        ...(state.xGoogleAnalytics.includeProductCategory && categoryFields), // Spread the dynamic category fields
                    };
                }),
            },
        };        

        const addToCartGoogleAnalyticsData = {
            event: state.xGoogleAnalytics.addToCartEventName,
            ...ecommerceAnalyticsData,
        };

        const quickOrderGoogleAnalyticsData = {
            event: state.xGoogleAnalytics.quickOrderEventName,
            ...ecommerceAnalyticsData,
        };

        if (isQuickOrderFlyout) {
            dataLayer.push(quickOrderGoogleAnalyticsData);
        }
        
        dataLayer.push(addToCartGoogleAnalyticsData);
    }
};

const _GaAddProductToCart = XngGaAddProductToCart as any;
addToEndOfChain(AddToCartChain, _GaAddProductToCart);
addToEndOfChain(AddWishListToCartChain, _GaAddProductToCart);
addToEndOfChain(AddCartLinesChain, _GaAddProductToCart);
addToEndOfChain(AddLinesToCartChain, _GaAddProductToCart);
addToEndOfChain(ReorderOrderDetailsChain, _GaAddProductToCart);
addToEndOfChain(ReorderOrderHistoryChain, _GaAddProductToCart);
addToEndOfChain(ReorderOrderStatusChain, _GaAddProductToCart);
addToEndOfChain(AddCartLineCollectionFromProductsChain, _GaAddProductToCart);
addToEndOfChain(AddLinesToCartRequisitionsChain, _GaAddProductToCart);
addToEndOfChain(PlaceOrderSavedOrderDetailsChain, _GaAddProductToCart);
